import { SelectionAction } from "@kuika/kuika-cl-model";

/** Every selection action the bar offers: its own icon buttons (`SELECTION_BAR_ACTIONS`) and the ⋯ rows. */
export type SelectionMenuKey =
  | "copy"
  | "paste"
  | "pasteStyle"
  | "duplicate"
  | "detach"
  | "selectParent"
  | "moveUp"
  | "moveDown"
  | "saveAsBlock"
  | "delete";

export interface ISelectionMenuIcon {
  name: string;
  type: "common" | "arrows";
}

export interface ISelectionMenuItem {
  key: SelectionMenuKey;
  label: string;
  icon: ISelectionMenuIcon;
  /** Shortcut hint shown on the right ("⌘C"); absent when the action has none. */
  shortcut?: string;
  danger?: boolean;
  /** Draws a divider above this row. */
  separatorBefore?: boolean;
  testId: string;
}

export interface ISelectionMenuContext {
  /** What the agent says is possible for this selection right now. */
  availableActions: readonly SelectionAction[];
  /** False for the screen root, which has no parent. */
  canSelectParent: boolean;
  /** Sibling-order rules (first/last sibling, fixed children) on top of `availableActions`. */
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** "⌘" on macOS, "Ctrl+" elsewhere. */
  modifierLabel: string;
}

interface IMenuRowSpec {
  key: SelectionMenuKey;
  label: string;
  icon: ISelectionMenuIcon;
  shortcut?: string;
  danger?: boolean;
  separatorBefore?: boolean;
}

const testIdFor = (key: SelectionMenuKey): string => `selectionMenu${key.charAt(0).toUpperCase()}${key.slice(1)}Option`;

/** The actions with their own icon button on the bar, in bar order; every other action is a ⋯ row. */
export const SELECTION_BAR_ACTIONS: readonly SelectionMenuKey[] = ["copy", "paste", "duplicate"];

/** `selectionCopyButton`, `selectionPasteButton`, `selectionDuplicateButton`. */
const barTestIdFor = (key: SelectionMenuKey): string => `selection${key.charAt(0).toUpperCase()}${key.slice(1)}Button`;

/** Whether one agent-reported action is a selection action at all. `renameBlock` belongs to the block toolbar. */
const isMenuAction = (action: SelectionAction): boolean => {
  switch (action) {
    case "copy":
    case "paste":
    case "pasteStyle":
    case "duplicate":
    case "detach":
    case "moveUp":
    case "moveDown":
    case "saveAsBlock":
    case "delete":
      return true;
    case "renameBlock":
      return false;
    default: {
      const unhandled: never = action;
      return unhandled;
    }
  }
};

/** Every action for one selected component, fixed order, each gated by what the selection allows. */
function buildSelectionActions(ctx: ISelectionMenuContext): IMenuRowSpec[] {
  const has = (action: SelectionAction): boolean => isMenuAction(action) && ctx.availableActions.includes(action);
  const mod: string = ctx.modifierLabel;
  const rows: IMenuRowSpec[] = [];

  if (has("copy"))
    rows.push({ key: "copy", label: "Copy", icon: { name: "copy", type: "common" }, shortcut: `${mod}C` });
  if (has("paste"))
    rows.push({ key: "paste", label: "Paste", icon: { name: "paste", type: "common" }, shortcut: `${mod}V` });
  if (has("pasteStyle"))
    rows.push({ key: "pasteStyle", label: "Paste style", icon: { name: "brush", type: "common" } });
  if (has("duplicate")) {
    rows.push({
      key: "duplicate",
      label: "Duplicate",
      icon: { name: "copy-plus", type: "common" },
      shortcut: `${mod}D`
    });
  }
  if (has("detach")) rows.push({ key: "detach", label: "Detach", icon: { name: "unlink", type: "common" } });
  if (ctx.canSelectParent) {
    rows.push({
      key: "selectParent",
      label: "Select parent",
      icon: { name: "corner-left-up", type: "common" },
      shortcut: "Esc"
    });
  }
  if (has("moveUp") && ctx.canMoveUp)
    rows.push({ key: "moveUp", label: "Move up", icon: { name: "arrow-up", type: "arrows" } });
  if (has("moveDown") && ctx.canMoveDown) {
    rows.push({ key: "moveDown", label: "Move down", icon: { name: "arrow-down", type: "arrows" } });
  }
  if (has("saveAsBlock"))
    rows.push({ key: "saveAsBlock", label: "Save as block", icon: { name: "package", type: "common" } });
  if (has("delete")) {
    rows.push({
      key: "delete",
      label: "Delete",
      icon: { name: "trash", type: "common" },
      shortcut: "⌫",
      danger: true,
      separatorBefore: rows.length > 0
    });
  }

  return rows;
}

/** The ⋯ menu: every allowed action that has no icon button of its own on the bar. */
export function buildSelectionMenu(ctx: ISelectionMenuContext): ISelectionMenuItem[] {
  const rows: IMenuRowSpec[] = buildSelectionActions(ctx).filter(
    (row: IMenuRowSpec): boolean => !SELECTION_BAR_ACTIONS.includes(row.key)
  );
  // The first row has nothing above it to be divided from.
  return rows.map((row: IMenuRowSpec, index: number): ISelectionMenuItem => ({
    ...row,
    separatorBefore: row.separatorBefore === true && index > 0,
    testId: testIdFor(row.key)
  }));
}

/** The bar's own action buttons (Copy · Paste · Duplicate), each shown only when allowed. */
export function buildSelectionBarActions(ctx: ISelectionMenuContext): ISelectionMenuItem[] {
  const rows: IMenuRowSpec[] = buildSelectionActions(ctx).filter((row: IMenuRowSpec): boolean =>
    SELECTION_BAR_ACTIONS.includes(row.key)
  );
  return rows.map((row: IMenuRowSpec): ISelectionMenuItem => ({ ...row, testId: barTestIdFor(row.key) }));
}
